import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowUpFromBracket, FaTrash, FaPencil, FaCheck, FaXmark, FaCircleXmark } from 'react-icons/fa6';
import { FaCheckCircle } from 'react-icons/fa';
import { LocalContactInfo } from '../../../../models/localContactService.model';
import { CommunityInvite } from '../../../../models/inviteService.model';
import { ProfileDataType, ProfileField } from '../../../../types/Profile';
import { isValidLocalContact, IsValidField, isEmptyField } from '../../../../utils/validationUtils';
import localContactService from '../../../../services/community/localContactService';
import IconBadge from '../../icons/IconBadge';
import CancelSaveButton from '../../misc/CancelSaveButton';
import ModalLayout from '../../layouts/ModalLayout';
import NameInput from '../../forms/profile/NameInput';
import ProfileFieldForm from '../../forms/ProfileFieldForm';
import CustomFieldsForm from '../../forms/CustomFieldsForm';
import { sortProfileFields } from '../../../../utils/sortUtils';
import PageHeader from '../../misc/PageHeader';

interface ReviewContactsHeaderProps {
  totalContacts: number;
  validContacts: number;
}

export function ReviewContactsHeader({
  totalContacts,
  validContacts,
}: ReviewContactsHeaderProps): JSX.Element {
  const { t } = useTranslation('', {
    keyPrefix: 'component.modal.importLocalContacts.review',
  });

  return (
    <PageHeader
      title={t('title')}
      subtitle={t('subtitle', { valid: validContacts, total: totalContacts })}
      />
  );
}

interface ReviewContactsViewProps {
  localContacts: LocalContactInfo[];
  setLocalContacts: (contacts: LocalContactInfo[]) => void;
  invite: CommunityInvite;
  onSubmitSuccessful?: () => void;
  onSubmitFailed?: () => void;
}

export function ReviewContactsView({
  localContacts,
  setLocalContacts,
  invite,
  onSubmitSuccessful,
  onSubmitFailed,
}: ReviewContactsViewProps): JSX.Element {
  const { t } = useTranslation();
  // modal-based editing state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingContact, setEditingContact] = useState<LocalContactInfo | null>(null);
  const [validateModal, setValidateModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const removeContact = (index: number) => {
    const newContacts = [...localContacts];
    newContacts.splice(index, 1);
    setLocalContacts(newContacts);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setValidateModal(true);
    // deep clone
    setEditingContact(JSON.parse(JSON.stringify(localContacts[index])));
  };

  const closeEditModal = () => {
    setEditingIndex(null);
    setEditingContact(null);
    setValidateModal(false);
  };

  const saveModalEdit = (updated: LocalContactInfo) => {
    if (editingIndex === null) return;
    const newContacts = [...localContacts];
    newContacts[editingIndex] = updated;
    setLocalContacts(newContacts);
    closeEditModal();
  };

  const stripInvalidFields = (contact: LocalContactInfo): LocalContactInfo => {
    // Strip name fields if invalid
    const firstName = contact.firstName || '';
    const lastName = contact.lastName || '';
    
    // Filter out invalid fields from receivedData
    const validReceivedData = contact.receivedData.filter((field) => {
      if (isEmptyField(field)) return true; // Keep empty fields
      return IsValidField(field); // Only keep valid non-empty fields
    });

    return {
      ...contact,
      firstName,
      lastName,
      receivedData: validReceivedData,
    };
  };

  const handleSubmit = async () => {
    const validContacts = localContacts.filter((c) => isValidLocalContact(c, invite));
    const invalidContacts = localContacts.filter((c) => !isValidLocalContact(c, invite));
    
    if (localContacts.length === 0) {
      toast.error(t('toast.error.noValidContacts'));
      return;
    }

    // If there are invalid contacts, show warning modal
    if (invalidContacts.length > 0) {
      setShowWarningModal(true);
      return;
    }

    // All contacts are valid, proceed directly
    setIsSubmitting(true);
    try {
      await localContactService.createLocalContacts(validContacts);
      toast.success(
        t('toast.success.importLocalContacts', {
          count: validContacts.length,
        })
      );
      onSubmitSuccessful?.();
    } catch (error) {
      toast.error(t('toast.error.importLocalContacts'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmImport = async () => {
    setShowWarningModal(false);
    setIsSubmitting(true);
    
    try {
      // Strip invalid fields from all contacts and import
      const processedContacts = localContacts
        .map(stripInvalidFields)
        .filter((c) => c.firstName && c.lastName); // Only import contacts with at least a name

      if (processedContacts.length === 0) {
        toast.error(t('toast.error.noValidContacts'));
        return;
      }

      await localContactService.createLocalContacts(processedContacts);
      toast.success(
        t('toast.success.importLocalContacts', {
          count: processedContacts.length,
        })
      );
      onSubmitSuccessful?.();
    } catch (error) {
      toast.error(t('toast.error.importLocalContacts'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldValue = (contact: LocalContactInfo, dataType: ProfileDataType): string => {
    const field = contact.receivedData.find((f) => f.dataType === dataType);
    if (!field) return '-';

    switch (dataType) {
      case ProfileDataType.EMAIL:
        return field.email || '-';
      case ProfileDataType.PHONENUMBER:
        return field.phoneNumber?.prefix && field.phoneNumber?.suffix
          ? `${field.phoneNumber.prefix}${field.phoneNumber.suffix}`
          : '-';
      case ProfileDataType.ADDRESS:
        return field.address
          ? `${field.address.street || ''} ${field.address.houseNumber || ''} ${field.address.houseNumberAddition || ''}, ${field.address.city || ''} ${field.address.postCode || ''}, ${field.address.country || ''}`.trim()
          : '-';
      case ProfileDataType.BIRTHDATE:
        return field.birthDate || '-';
      case ProfileDataType.BUSINESSNAME:
        return field.businessName || '-';
      default:
        return '-';
    }
  };

  const getFieldError = (contact: LocalContactInfo, dataType: ProfileDataType): string | null => {
    const field = contact.receivedData.find((f) => f.dataType === dataType);
    if (!field || isEmptyField(field)) return null;
    
    if (!IsValidField(field)) {
      switch (dataType) {
        case ProfileDataType.EMAIL:
          return t('toast.error.field.email');
        case ProfileDataType.PHONENUMBER:
          return t('toast.error.field.phoneNumber');
        case ProfileDataType.ADDRESS:
          return t('toast.error.field.address');
        case ProfileDataType.BIRTHDATE:
          return t('toast.error.field.birthDate');
        default:
          return t('toast.error.field.invalid');
      }
    }
    return null;
  };

  const getContactErrors = (contact: LocalContactInfo): string[] => {
    const errors: string[] = [];
    
    if (!contact.firstName) {
      errors.push(t('toast.error.field.firstName'));
    }
    if (!contact.lastName) {
      errors.push(t('toast.error.field.lastName'));
    }

    contact.receivedData.forEach((field) => {
      if (!isEmptyField(field) && !IsValidField(field)) {
        const error = getFieldError(contact, field.dataType);
        if (error) errors.push(error);
      }
    });

    return errors;
  };

  const getDataTypeTranslationKey = (dataType: ProfileDataType): string => {
    const keyMap: Record<ProfileDataType, string> = {
      [ProfileDataType.EMAIL]: 'email',
      [ProfileDataType.PHONENUMBER]: 'phoneNumber',
      [ProfileDataType.ADDRESS]: 'address',
      [ProfileDataType.BIRTHDATE]: 'birthDate',
      [ProfileDataType.BUSINESSNAME]: 'businessName',
      [ProfileDataType.SOCIAL]: 'social',
    };
    return keyMap[dataType] || dataType.toLowerCase();
  };

  const validContacts = localContacts.filter((c) => isValidLocalContact(c, invite));
  const allDataTypes = new Set<ProfileDataType>();
  
  [...invite.mandatoryFields, ...invite.optionalFields].forEach((field) => {
    if (Object.values(ProfileDataType).includes(field as ProfileDataType)) {
      allDataTypes.add(field as ProfileDataType);
    }
  });

  localContacts.forEach((contact) => {
    contact.receivedData.forEach((field) => {
      if (!isEmptyField(field)) {
        allDataTypes.add(field.dataType);
      }
    });
  });

  const displayDataTypes = Array.from(allDataTypes);

  return (
    <>
      <div className="mb-8">
        <ReviewContactsHeader
          totalContacts={localContacts.length}
          validContacts={validContacts.length}
        />
      </div>
      <div className="flex flex-col gap-6">
      {/* Table */}
      <div className="relative w-full overflow-x-auto">
          <table className="z-0 h-full min-w-full divide-y divide-primary-300 border-y border-primary-300">
            <thead className="z-0">
              <tr>
                <th className="py-3 pl-2 text-left text-sm font-medium tracking-wider text-primary-900">
                  #
                </th>
                <th className="py-3 pl-2 text-left text-sm font-medium tracking-wider text-primary-900">
                  {t('component.modal.importLocalContacts.review.firstName')}
                </th>
                <th className="py-3 pl-2 text-left text-sm font-medium tracking-wider text-primary-900">
                  {t('component.modal.importLocalContacts.review.lastName')}
                </th>
              {displayDataTypes.map((dataType) => (
                <th
                  key={dataType}
                  className="py-3 pl-2 text-left text-sm font-medium tracking-wider text-primary-900">
                  {t(`dataType.${getDataTypeTranslationKey(dataType)}`)}
                </th>
              ))}
              <th className="py-3 pl-2 text-left text-sm font-medium tracking-wider text-primary-900">
                {t('component.modal.importLocalContacts.review.status')}
              </th>
              <th className="py-3 pr-2 text-right text-sm font-medium tracking-wider text-primary-900">
                {t('component.modal.importLocalContacts.review.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="z-0">
            {localContacts.map((contact, index) => {
              const errors = getContactErrors(contact);
              const isValid = errors.length === 0;

              return (
                <tr
                  key={index}
                  className={`group ${isValid ? '' : 'bg-red-50'}`}>
                  <td className="whitespace-nowrap bg-secondary-50 py-4 pl-2 text-sm font-medium">
                    {index + 1}
                  </td>
                  <td className="whitespace-nowrap bg-secondary-50 py-4 pl-2 text-sm font-medium text-primary-900">
                    <span className={!contact.firstName ? 'font-semibold text-danger' : ''}>
                      {contact.firstName || '-'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap bg-secondary-50 py-4 pl-2 text-sm font-medium text-primary-900">
                    <span className={!contact.lastName ? 'font-semibold text-danger' : ''}>
                      {contact.lastName || '-'}
                    </span>
                  </td>
                  {displayDataTypes.map((dataType) => {
                    const value = getFieldValue(contact, dataType);
                    const error = getFieldError(contact, dataType);
                    
                    return (
                      <td
                        key={dataType}
                        className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap bg-secondary-50 py-4 pl-2 text-sm font-medium text-primary-900"
                        title={value}>
                          <span className={error ? 'font-semibold text-danger' : ''}>{value}</span>
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap bg-secondary-50 py-4 pl-2 text-sm font-medium text-primary-900">
                    {isValid ? (
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-medium bg-success text-white">
                        <FaCheckCircle className="h-3 w-3" />
                        {t('component.modal.importLocalContacts.review.valid')}
                      </span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-medium bg-danger text-white">
                          <FaCircleXmark className="h-3 w-3" />
                          {t('component.modal.importLocalContacts.review.invalid')}
                        </span>
                        {errors.map((error, i) => (
                          <span key={i} className="text-xs text-danger">
                            {error}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap bg-secondary-50 py-4 pr-2 text-right text-sm font-medium text-primary-900">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(index)}
                        className="rounded-lg p-2 text-primary"
                        title={t('component.modal.importLocalContacts.review.edit')}>
                        <FaPencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="rounded-lg p-2 text-danger"
                        title={t('component.modal.importLocalContacts.review.remove')}>
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

      </div>

      {/* Edit contact modal */}
      {editingContact && (
        <ModalLayout
          open={!!editingContact}
          setOpen={() => closeEditModal()}
          closeButton
          className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-6 sm:max-w-[700px]">
          <div className="flex border-b border-secondary-200 pb-4">
            <IconBadge icon={FaPencil} />
            <div className="mb-4 ml-4 flex w-full flex-col">
              <h2 className="text-2xl">{t('component.modal.localContact.edit.title')}</h2>
              <p>{t('component.modal.localContact.edit.subtitle')}</p>
            </div>
          </div>

          <div className="mt-4 flex w-full flex-col gap-4">
            <NameInput
              alias={`${editingContact.firstName || ''}+${editingContact.lastName || ''}`}
              validate={validateModal}
              setAlias={(s) => {
                const [firstName, lastName] = s.split('+');
                setEditingContact({ ...editingContact, firstName, lastName } as LocalContactInfo);
              }}
            />

            {sortProfileFields(editingContact.receivedData).map((f, idx) => {
              const isFieldEmpty = isEmptyField(f);
              const isFieldValid = IsValidField(f);
              const showError = validateModal && !isFieldEmpty && !isFieldValid;

              return (
                <div
                  key={idx}
                  className='relative rounded-lg transition-all'>
                  <ProfileFieldForm
                    field={f}
                    canBeEmpty
                    validate={validateModal}
                    setField={(newField: any) => {
                      const receivedData = editingContact.receivedData.map((oldField) =>
                        oldField.dataType === newField.dataType ? newField : oldField,
                      );
                      setEditingContact({ ...editingContact, receivedData } as LocalContactInfo);
                    }}
                  />
                  {showError && (
                    <button
                      type="button"
                      onClick={() => {
                        const emptyField: ProfileField = {
                          ...f,
                          email: f.dataType === ProfileDataType.EMAIL ? '' : f.email,
                          phoneNumber: f.dataType === ProfileDataType.PHONENUMBER ? { prefix: '', suffix: '', countryCode: '' } : f.phoneNumber,
                          address: f.dataType === ProfileDataType.ADDRESS ? {
                            street: '',
                            houseNumber: '',
                            houseNumberAddition: '',
                            postCode: '',
                            city: '',
                            country: '',
                            countryCode: '',
                          } : f.address,
                          birthDate: f.dataType === ProfileDataType.BIRTHDATE ? '' : f.birthDate,
                          businessName: f.dataType === ProfileDataType.BUSINESSNAME ? '' : f.businessName,
                        };
                        const receivedData = editingContact.receivedData.map((oldField) =>
                          oldField.dataType === emptyField.dataType ? emptyField : oldField,
                        );
                        setEditingContact({ ...editingContact, receivedData } as LocalContactInfo);
                      }}
                      className="absolute right-4 top-4 flex items-center gap-2 rounded-lg bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors">
                      <FaXmark className="h-3 w-3" />
                      {t('component.modal.localContact.excludeField')}
                    </button>
                  )}
                </div>
              );
            })}

            <CustomFieldsForm
              fields={invite.customFields || []}
              response={editingContact.customFieldResponses}
              setResponse={(newResponse) =>
                setEditingContact({ ...editingContact, customFieldResponses: newResponse } as LocalContactInfo)
              }
            />

            <div className="flex flex-col gap-2">
              <p className="mb-2 text-lg font-medium">{t('component.modal.localContact.noteTitle')}</p>
              <textarea
                className="mb-2 h-32 w-full resize-none rounded-lg placeholder-secondary-200"
                placeholder={t('component.modal.localContact.notePlaceholder')}
                onChange={(e) => setEditingContact({ ...editingContact, note: e.target.value } as LocalContactInfo)}
                value={editingContact.note}
              />
            </div>
          </div>

          <div className="mt-4 flex w-full items-center justify-end gap-2">
            <p className={`w-fit justify-end text-sm text-danger ${(!validateModal || isValidLocalContact(editingContact, invite)) ? 'invisible' : ''}`}>
              {t('component.modal.localContact.invalid')}
            </p>
            <CancelSaveButton
              onCancel={() => closeEditModal()}
              onSave={() => {
                setValidateModal(true);
                if (isValidLocalContact(editingContact, invite)) saveModalEdit(editingContact);
              }}
            />
          </div>
        </ModalLayout>
      )}

      {/* Summary */}
      <div className="rounded-xl bg-secondary-100 p-5">
        <div>
          <p className="font-semibold">
            {t('component.modal.importLocalContacts.review.summary', {
              total: localContacts.length,
              valid: validContacts.length,
              invalid: localContacts.length - validContacts.length,
            })}
          </p>
          {validContacts.length < localContacts.length && (
            <p className="mt-2 text-sm">
              {t('component.modal.importLocalContacts.review.invalidFieldsWarning')}
            </p>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end border-t border-primary-300 pt-6">
        <CancelSaveButton
          onCancel={() => onSubmitFailed?.()}
          onSave={handleSubmit}
          loading={isSubmitting}
        />
      </div>

      {/* Warning modal for invalid contacts */}
      {showWarningModal && (
        <ModalLayout
          open={showWarningModal}
          setOpen={setShowWarningModal}
          closeButton
          className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-6 sm:max-w-[500px]">
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-semibold">
              {t('component.modal.importLocalContacts.warning.title')}
            </h2>
            <p>
              {t('component.modal.importLocalContacts.warning.message', {
                invalid: localContacts.length - validContacts.length,
                total: localContacts.length,
              })}
            </p>
            <p className="text-sm">
              {t('component.modal.importLocalContacts.warning.explanation')}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowWarningModal(false)}
                disabled={isSubmitting}
                className="rounded-[12px] bg-secondary-200 px-6 py-2.5 text-sm font-semibold disabled:opacity-50">
                {t('component.modal.importLocalContacts.warning.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmImport}
                disabled={isSubmitting}
                className="flex items-center gap-2 rounded-[12px] bg-primary px-6 py-2.5 text-sm font-semibold text-secondary-50 disabled:opacity-50">
                {isSubmitting && (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                )}
                {t('component.modal.importLocalContacts.warning.continue')}
              </button>
            </div>
          </div>
        </ModalLayout>
      )}
    </div>
    </>
  );
}
